import { apiUrl } from './config';
import { ApiError, toApiError } from './errors';
import { refreshAccessToken } from './refresh-interceptor';
import { getAccessToken, notifySessionEnded } from './token-store';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * Serialised as JSON. Omit for GET.
   *
   * A `FormData` is the one exception: it is passed to `fetch` untouched, so a
   * file upload goes up as real `multipart/form-data` rather than as a base64
   * string wedged into a JSON body. See `buildInit`.
   */
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Opt out of the 401 -> refresh -> replay dance. Used by the auth endpoints
   * themselves: a failed login must surface INVALID_CREDENTIALS, not trigger a
   * refresh of a session that does not exist.
   */
  skipAuthRefresh?: boolean;
  /**
   * Extra request headers — currently only `Idempotency-Key`.
   *
   * Deliberately headers and not a body field: idempotency is a property of the
   * REQUEST, not of the thing being created, and it must survive the 401 replay
   * below unchanged. `buildInit` runs twice for a replayed request, so anything
   * put here is re-sent verbatim, which is exactly what an idempotency key
   * needs — a refreshed token must not turn one payment into two.
   */
  headers?: Readonly<Record<string, string>>;
}

function buildInit(options: ApiRequestOptions, token: string | null): RequestInit {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };

  if (token !== null) {
    // The access token travels as a header, never as a cookie. Because
    // attaching it requires explicit JS, API calls are inherently immune to
    // CSRF — a cross-site form post cannot add this header.
    headers['Authorization'] = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    // Always `include`: the refresh cookie is Path=/api/auth so it rides along
    // only on the auth routes, but the flag has to be set for it to ride at all.
    credentials: 'include',
  };

  if (options.body !== undefined) {
    if (options.body instanceof FormData) {
      /**
       * NO `Content-Type` HEADER HERE — deliberately.
       *
       * multipart/form-data is only parseable with the `boundary=...` parameter
       * the browser generates when it serialises the FormData. Setting the
       * header by hand omits that boundary, and every multipart parser on the
       * far side then sees a body it cannot split into parts: the upload fails
       * as "no file" rather than as a network error, which is a genuinely
       * confusing thing to debug.
       *
       * Passing the same FormData object through `buildInit` twice is safe —
       * fetch re-serialises it per call — so the 401 refresh-and-replay below
       * survives an upload intact.
       */
      init.body = options.body;
    } else {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }
  }

  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  return init;
}

async function parseSuccess<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (text.length === 0) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

/**
 * Should a 401 be treated as "my access token aged out, refresh and replay"?
 *
 * Exported so it can be unit-tested without a fetch harness. A 401 with no
 * parseable code is included deliberately: an expired token is by far the most
 * likely cause, and the worst case is one wasted refresh attempt that then
 * fails cleanly. FORCE_REAUTH is excluded because the server has already
 * destroyed the token family — refreshing would only 401 again.
 */
export function shouldAttemptRefresh(error: ApiError): boolean {
  if (error.status !== 401) return false;
  if (error.code === 'FORCE_REAUTH') return false;
  return error.code === 'TOKEN_EXPIRED' || error.code === null;
}

/**
 * Is this the server telling us the session is GONE, not merely stale?
 *
 * Exported for tests. Kept separate from `shouldAttemptRefresh` because the two
 * answer different questions and only happen to look at the same field: one
 * decides whether to retry, this one decides whether to end the session.
 */
export function isSessionEnded(error: ApiError): boolean {
  return error.status === 401 && error.code === 'FORCE_REAUTH';
}

/**
 * FORCE_REAUTH on ANY endpoint means the server destroyed the session — the
 * refresh family was revoked by a logout, by reuse detection, or by an admin
 * evicting this user. There is no credential left to recover with.
 *
 * This used to be unreachable outside `/auth/refresh`, so `apiFetch` simply
 * rethrew it and whatever screen made the call rendered a raw error while the
 * app went on believing it was signed in. Server-side session revocation makes
 * it reachable on every route, so it has to be handled as what it is: the end of
 * the session. `notifySessionEnded` clears the in-memory token and flips
 * `AuthProvider` to `anonymous`, which `RequireAuth` turns into a redirect to
 * /login with an explanatory notice.
 *
 * The error is still thrown afterwards — the caller's `await` must not resolve
 * as though the request succeeded.
 */
function endSessionIfRevoked(error: ApiError): void {
  if (isSessionEnded(error)) {
    notifySessionEnded('FORCE_REAUTH');
  }
}

/**
 * The authenticated request itself, returning the RAW `Response`.
 *
 * This is where the 401 dance lives: refresh exactly once, then replay the
 * original request exactly once. There is no retry loop — if the replay 401s as
 * well, something is wrong that another round trip will not fix, and looping
 * would hammer the rotation endpoint.
 *
 * Split out of `apiFetch` for the PDF downloads. `apiFetch` parses every success
 * as JSON, which a PDF body is not, so `download.ts` used to bypass the client
 * entirely and hand-roll a `fetch` — and with it lost the refresh-and-replay,
 * making every download 401 permanently the moment the in-memory access token
 * aged out. Callers that need bytes rather than JSON now take THIS function, so
 * there is exactly one implementation of the auth retry in the app rather than
 * one per body type.
 *
 * Contract: resolves only with an `ok` response. Any failure — including one the
 * refresh could not repair — throws `ApiError`, so a caller never has to check
 * `response.ok` itself.
 */
export async function apiFetchResponse(
  path: string,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const response = await fetch(apiUrl(path), buildInit(options, getAccessToken()));

  if (response.ok) {
    return response;
  }

  const error = await toApiError(response);

  if (options.skipAuthRefresh === true || !shouldAttemptRefresh(error)) {
    // Runs for the auth endpoints too: `skipAuthRefresh` opts out of RETRYING,
    // not out of noticing that the session has been destroyed.
    endSessionIfRevoked(error);
    throw error;
  }

  // Concurrent 401s all land here and all await the SAME promise — see
  // refresh-interceptor.ts for why more than one refresh is a logout bug.
  const freshToken = await refreshAccessToken();

  const replay = await fetch(apiUrl(path), buildInit(options, freshToken));
  if (replay.ok) {
    return replay;
  }

  // A refresh that succeeded followed by a FORCE_REAUTH on the replay means the
  // session died between the two — an admin eviction landing mid-flight, or a
  // logout in another tab. Same terminal handling, no second retry.
  const replayError = await toApiError(replay);
  endSessionIfRevoked(replayError);
  throw replayError;
}

/**
 * The single entry point for every JSON API call in the app.
 *
 * Thin wrapper over `apiFetchResponse`: same auth behaviour, plus JSON parsing.
 */
export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return parseSuccess<T>(await apiFetchResponse(path, options));
}
